import { useCallback, useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  History,
  UploadCloud,
  CheckCircle2,
  XCircle,
  KeyRound,
  Mail,
  UserPlus,
  ShieldCheck,
  RefreshCw,
  Download,
} from "lucide-react";

const EVENT_META = {
  "user.register": { label: "Account registered", icon: UserPlus, tone: "text-slate-700 bg-slate-100" },
  "user.invited": { label: "Teammate invited", icon: UserPlus, tone: "text-teal-700 bg-teal-50" },
  "user.forgot_password_requested": { label: "Password reset requested", icon: KeyRound, tone: "text-amber-700 bg-amber-50" },
  "user.password_reset": { label: "Password reset completed", icon: KeyRound, tone: "text-emerald-700 bg-emerald-50" },
  "document.uploaded": { label: "Document uploaded", icon: UploadCloud, tone: "text-slate-700 bg-slate-100" },
  "document.approved": { label: "Document approved", icon: CheckCircle2, tone: "text-emerald-700 bg-emerald-50" },
  "document.rejected": { label: "Document rejected", icon: XCircle, tone: "text-rose-700 bg-rose-50" },
  "nmc.pin_set": { label: "NMC PIN set", icon: ShieldCheck, tone: "text-indigo-700 bg-indigo-50" },
  "nmc.check_recorded": { label: "NMC monthly check", icon: ShieldCheck, tone: "text-indigo-700 bg-indigo-50" },
  "dbs.check_recorded": { label: "DBS Update Service check", icon: ShieldCheck, tone: "text-teal-700 bg-teal-50" },
  "notification.sent": { label: "Notification sent", icon: Mail, tone: "text-teal-700 bg-teal-50" },
};

const EVENT_OPTIONS = [
  { value: "__all__", label: "All events" },
  { value: "document.uploaded", label: "Document uploaded" },
  { value: "document.approved", label: "Document approved" },
  { value: "document.rejected", label: "Document rejected" },
  { value: "user.forgot_password_requested", label: "Password reset requested" },
  { value: "user.password_reset", label: "Password reset completed" },
  { value: "user.register", label: "Account registered" },
  { value: "user.invited", label: "Teammate invited" },
  { value: "nmc.pin_set", label: "NMC PIN set" },
  { value: "nmc.check_recorded", label: "NMC monthly check" },
  { value: "dbs.check_recorded", label: "DBS Update Service check" },
  { value: "notification.sent", label: "Notification sent" },
];

function formatMeta(entry) {
  const m = entry.metadata || {};
  const bits = [];
  if (m.item_key) bits.push(m.item_key.replaceAll("_", " "));
  if (m.reason) bits.push(`reason: ${m.reason}`);
  if (m.kind) bits.push(`(${m.kind})`);
  if (m.expiry_date) bits.push(`expires ${m.expiry_date}`);
  if (m.email) bits.push(m.email);
  if (m.role) bits.push(`as ${m.role}`);
  if (m.worker_role) bits.push(m.worker_role.replaceAll("_", " "));
  if (m.auth_provider) bits.push(m.auth_provider);
  return bits.join(" · ");
}

function toCsv(entries) {
  const header = ["created_at", "event", "actor_name", "actor_role", "target_type", "target_id", "metadata"];
  const rows = entries.map((e) => [
    e.created_at,
    e.event,
    (e.actor_name || "").replace(/"/g, '""'),
    e.actor_role || "",
    e.target_type || "",
    e.target_id || "",
    JSON.stringify(e.metadata || {}).replace(/"/g, '""'),
  ]);
  return [header, ...rows]
    .map((r) => r.map((v) => `"${v ?? ""}"`).join(","))
    .join("\n");
}

export default function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextBefore, setNextBefore] = useState(null);
  const [event, setEvent] = useState("__all__");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (event !== "__all__") params.event = event;
      const { data } = await api.get("/audit-log", { params });
      setEntries(data.items || []);
      setNextBefore(data.next_before || null);
    } finally {
      setLoading(false);
    }
  }, [event]);

  const loadMore = useCallback(async () => {
    if (!nextBefore) return;
    setLoadingMore(true);
    try {
      const params = { limit: 100, before: nextBefore };
      if (event !== "__all__") params.event = event;
      const { data } = await api.get("/audit-log", { params });
      setEntries((prev) => [...prev, ...(data.items || [])]);
      setNextBefore(data.next_before || null);
    } finally {
      setLoadingMore(false);
    }
  }, [event, nextBefore]);

  useEffect(() => { load(); }, [load]);

  const filtered = entries.filter((e) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (e.actor_name || "").toLowerCase().includes(q) ||
      (e.event || "").toLowerCase().includes(q) ||
      JSON.stringify(e.metadata || {}).toLowerCase().includes(q)
    );
  });

  const download = () => {
    const csv = toCsv(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audentra-audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppLayout>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-caps text-slate-500">Compliance</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-1">
            Audit log
          </h1>
          <p className="text-slate-600 mt-1.5 max-w-2xl">
            A tamper-evident trail of every upload, review, password reset, and notification —
            ready for CQC inspection and internal governance review.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={load}
            data-testid="audit-refresh"
            className="rounded-full h-10 px-4"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
          <Button
            onClick={download}
            data-testid="audit-export-csv"
            className="bg-teal-700 hover:bg-teal-800 rounded-full h-10 px-5"
          >
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="label-caps text-slate-500 mb-1.5">Search</div>
          <Input
            data-testid="audit-search"
            placeholder="Search by actor, event, or metadata"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-56">
          <div className="label-caps text-slate-500 mb-1.5">Event</div>
          <Select value={event} onValueChange={setEvent}>
            <SelectTrigger data-testid="audit-event-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EVENT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="audit-list">
        <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-slate-50 text-slate-500 label-caps text-xs">
          <div className="col-span-3">When</div>
          <div className="col-span-3">Event</div>
          <div className="col-span-2">Actor</div>
          <div className="col-span-4">Details</div>
        </div>

        {loading && (
          <div className="px-5 py-10 text-slate-500" data-testid="audit-loading">Loading…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="px-5 py-10 text-slate-500" data-testid="audit-empty">
            <History className="inline h-4 w-4 mr-2 text-slate-400" />
            No audit events match your filter yet.
          </div>
        )}
        {!loading &&
          filtered.map((e) => {
            const meta = EVENT_META[e.event] || {
              label: e.event,
              icon: History,
              tone: "text-slate-700 bg-slate-100",
            };
            const Icon = meta.icon;
            return (
              <div
                key={e.id}
                className="grid grid-cols-12 gap-4 px-5 py-4 border-t border-slate-100 hover:bg-slate-50 text-sm"
                data-testid={`audit-row-${e.id}`}
              >
                <div className="col-span-3 text-slate-700">
                  <div className="font-medium">
                    {new Date(e.created_at).toLocaleString()}
                  </div>
                  <div className="text-xs text-slate-400">{e.event}</div>
                </div>
                <div className="col-span-3">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${meta.tone}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {meta.label}
                  </span>
                </div>
                <div className="col-span-2 text-slate-700 truncate">
                  <div className="font-medium truncate">{e.actor_name || "—"}</div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider">
                    {e.actor_role || (e.actor_id ? "user" : "system")}
                  </div>
                </div>
                <div className="col-span-4 text-slate-600 text-xs break-words">
                  {formatMeta(e) || <span className="text-slate-400">—</span>}
                  {e.target_id && (
                    <div className="text-[10px] text-slate-400 mt-1 font-mono">
                      {e.target_type}:{e.target_id.slice(0, 8)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>

      {nextBefore && !loading && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="outline"
            onClick={loadMore}
            disabled={loadingMore}
            data-testid="audit-load-more"
            className="rounded-full h-10 px-6"
          >
            {loadingMore ? "Loading…" : "Load older entries"}
          </Button>
        </div>
      )}
    </AppLayout>
  );
}
